import React, { memo } from "react";
import PropTypes from "prop-types";
import { getIn } from "formik";
import cx from "classnames";

import { CustomRadioButton as Radio } from "../RadioButton/CustomRadioButton";
import { ContexualHelp, ErrorMessage } from "../../Notifications";
import { areEqualFieldProps } from "../utils";
import { StyledInlineRadioGroupForm, StyledInlineRadioGroup, StyledLabel } from "./styled";

const InlineRadioGroupBase = ({
  label,
  field,
  form: { touched, errors, setFieldValue },
  options,
  required,
  onChange,
  contextualHelpText,
  contextualHelpProps = {},
  onSelect = () => {},
  disabled: isDisabled,
  customIcon = false,
  classes: extendedClasses,
  radioColor = "secondary",
  dataTestId,
  nestedFieldError = false
}) => {
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);
  const handleChange = event =>
    onChange ? onChange(event) : setFieldValue(field.name, JSON.parse(event.target.value));
  const disabledRadioButtonByDefault = ["All of us", "Any of us", "Other (please specify)"];

  return (
    <StyledInlineRadioGroupForm
      component="fieldset"
      required={required}
      error={isError}
      data-testid={dataTestId}
    >
      <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
        <StyledInlineRadioGroup
          aria-label={label}
          name={field.name}
          value={JSON.stringify(field.value)}
          onChange={handleChange}
          className={cx("smallText", isDisabled && "disabledLabel")}
          data-testid={dataTestId}
        >
          {label && <StyledLabel>{label}</StyledLabel>}
          <div
            className={cx("inlineFormRadioWrapper", extendedClasses?.parent)}
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "8px"
            }}
          >
            {options.map(item => (
              <Radio
                disabled={isDisabled || disabledRadioButtonByDefault.includes(item.label)}
                radioColor={radioColor}
                key={item.key}
                value={JSON.stringify(item.value)}
                label={item.label || item.displayText}
                onSelect={onSelect}
                customIcon={customIcon}
                classes={{
                  ...extendedClasses,
                  root: cx(
                    field.value === item.value && "selectedRadioButton",
                    extendedClasses?.root
                  )
                }}
                data-testid={item.key}
                selected={item.value === field.value}
              />
            ))}
          </div>
        </StyledInlineRadioGroup>
      </ContexualHelp>

      {isError && <ErrorMessage error={errorMessage} />}
      {nestedFieldError && !isError && <ErrorMessage error={nestedFieldError} />}
    </StyledInlineRadioGroupForm>
  );
};

InlineRadioGroupBase.propTypes = {
  label: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.any
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  customIcon: PropTypes.bool,
  classes: PropTypes.object,
  radioColor: PropTypes.string,
  dataTestId: PropTypes.string,
  nestedFieldError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

export const InlineRadioGroup = memo(InlineRadioGroupBase, areEqualFieldProps);
